import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { WorkflowDeadlineComponent } from '@app/shared/components/workflow-deadline/workflow-deadline.component';
import type { WorkflowDeadlineBinding } from '@app/shared/workflow-deadlines/workflow-deadline.models';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, type Observable } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { WorkflowTimersService } from './workflow-timers.service';
import {
  workflowTimerStateLabel,
  type WorkflowTimerChangeRequest,
  type WorkflowTimerOperation,
  type WorkflowTimerRead,
} from './workflow-timers.models';
import {
  workflowTimerListV2,
  workflowTimerV2,
  type WorkflowTimerReadV2,
} from './workflow-timers-v2.models';
import { mapWorkflowRegistryDiagnosticError } from '../platform-admin/workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { WorkflowReasonCaptureComponent } from '@app/shared/components/workflow-reason-capture/workflow-reason-capture.component';
import {
  WorkflowReasonCaptureDialogService,
  type WorkflowReasonCaptureDialogHandle,
} from '@app/shared/components/workflow-reason-capture/workflow-reason-capture-dialog.service';
import {
  timerReasonCaptureContract,
  type WorkflowReasonCapturePresentation,
  type WorkflowReasonCaptureValue,
} from '@app/shared/workflow-commands/workflow-reason-capture';
import {
  resolveTimerReasonCatalog,
  type WorkflowReasonResolution,
} from '@app/shared/workflow-commands/workflow-reason-catalog';

type ViewedTimer = WorkflowTimerRead | WorkflowTimerReadV2;

@Component({
  selector: 'phms-workflow-timers-panel',
  standalone: true,
  imports: [
    EzStandardStateComponent,
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    WorkflowReasonCaptureComponent,
    WorkflowDeadlineComponent,
  ],
  template: `<section
    class="timers-panel"
    aria-label="Step timer history"
    [attr.aria-busy]="loading || busy"
  >
    <header>
      <h2>Step timers</h2>
      <button
        uiButton
        type="button"
        (click)="refresh()"
        [disabled]="
          busy ||
          loading ||
          interactionBlocked ||
          previewMode ||
          !supportedReadVersion()
        "
      >
        {{ loaded ? 'Refresh timers' : 'View timers and history' }}
      </button>
    </header>
    <p *ngIf="!loaded && !loading && !readErrorKind">
      Read this visit's retained deadlines and available timer controls.
    </p>
    <p *ngIf="previewMode">
      Synthetic preview only. No live timer is read or changed.
    </p>
    <ui-standard-state
      *ngIf="loading || readErrorKind"
      [kind]="loading ? 'loading' : readErrorKind!"
      [message]="loading ? 'Loading timers…' : error"
    />
    <p *ngIf="error && !readErrorKind" role="alert">{{ error }}</p>
    <p *ngIf="notice" role="status">{{ notice }}</p>
    <ui-standard-state
      *ngIf="loaded && !loading && !readErrorKind && !rows.length"
      kind="empty"
      message="No timer is recorded for this visit."
    />
    <article *ngFor="let timer of rows">
      <header>
        <h3>{{ timer.label }}</h3>
        <strong
          >{{ stateLabel(timer.state) }} · generation
          {{ timer.generation }}</strong
        >
      </header>
      @if (readVersion === 2 || timer.deadline !== undefined) {
        <phms-workflow-deadline
          [deadline]="timer.deadline"
          [binding]="deadlineBinding(timer)"
          [showLabel]="false"
          [projectionVersion]="readVersion === 2 ? 2 : 1"
        />
      } @else {
        <dl>
          <div>
            <dt>Started</dt>
            <dd>{{ timer.anchorUtc | date: 'medium' }}</dd>
          </div>
          <div>
            <dt>Warning due</dt>
            <dd>{{ timer.warningDueUtc | date: 'medium' }}</dd>
          </div>
          <div>
            <dt>Breach due</dt>
            <dd>{{ timer.breachDueUtc | date: 'medium' }}</dd>
          </div>
        </dl>
      }

      <p *ngIf="stageSummary(timer) as summary" class="hint">{{ summary }}</p>
      <p *ngIf="controlsUnavailable(timer) as unavailable" class="hint">
        {{ unavailable }}
      </p>

      <p *ngIf="timer.state === 'paused'">
        Paused. These due instants are retained; resume shifts them by the
        paused elapsed time.
      </p>
      <p *ngIf="timer.warningCapturedUtc">
        Warning action captured {{ timer.warningCapturedUtc | date: 'medium' }}.
      </p>
      <p *ngIf="timer.breachCapturedUtc">
        Breach action captured {{ timer.breachCapturedUtc | date: 'medium' }}.
      </p>
      <p
        *ngIf="timer.warningCapturedUtc || timer.breachCapturedUtc"
        class="hint"
      >
        Capture queues the registered operation; it does not confirm delivery.
        Inspect attempts in Jobs &amp; outbox.
      </p>
      <p *ngIf="timer.issueCode" class="issue">
        Timer processing needs attention. Refresh current state or inspect its
        recorded operation.
      </p>
      <div class="actions" *ngIf="timer.availableActions.length">
        <button
          uiButton
          type="button"
          *ngFor="let action of supportedActions(timer)"
          (click)="choose(timer, action)"
          [disabled]="busy || interactionBlocked || stale"
        >
          {{ actionLabel(action) }}
        </button>
      </div>
    </article>
    <section
      *ngIf="!selection && reason.trim()"
      aria-label="Retained timer reason"
    >
      <p>
        Your entered reason is kept. Select a current timer action after
        refreshing, or discard this input.
      </p>
      <p *ngIf="reasonMode === 'catalog'" class="hint">
        Earlier selection: {{ reasonLabel || reason }} ({{ reason }}). Choose a
        reason again for a current action.
      </p>
      <label *ngIf="reasonMode === 'text'"
        >Retained reason<textarea
          uiControl
          rows="2"
          maxlength="500"
          [ngModel]="reason"
          (ngModelChange)="reasonChanged($event)"
          [ngModelOptions]="standalone"
          [disabled]="busy || interactionBlocked"
        ></textarea>
      </label>
      <button uiButton type="button" (click)="cancel()" [disabled]="busy">
        Discard retained reason
      </button>
    </section>
    <section *ngIf="selection as selected" aria-label="Timer change">
      <h3>{{ actionLabel(selected.operation) }} · {{ selected.label }}</h3>
      <app-workflow-reason-capture
        [presentation]="reasonPresentation()!"
        [value]="reasonValue()"
        [disabled]="busy || interactionBlocked || stale"
        (valueChange)="captureReasonChanged($event)"
      />
      <button
        uiButton
        type="button"
        (click)="editReason()"
        [disabled]="
          busy || interactionBlocked || stale || !!selected.reason.error
        "
      >
        {{ reason ? 'Edit reason in dialog' : 'Add reason in dialog' }}
      </button>
      <p *ngIf="stale" role="status">
        The viewed timer changed. Refresh and select a current action. Your
        reason is kept.
      </p>
      <div class="actions">
        <button
          uiButton
          type="button"
          (click)="submit()"
          [disabled]="busy || interactionBlocked || stale || !canSubmitReason()"
        >
          {{ busy ? 'Saving…' : actionLabel(selected.operation) }}</button
        ><button uiButton type="button" (click)="cancel()" [disabled]="busy">
          Cancel timer change
        </button>
      </div>
    </section>
  </section>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }
      .timers-panel {
        display: grid;
        gap: 0.7rem;
        border-top: 1px solid var(--custom-border-color);
        padding-top: 0.75rem;
      }
      header,
      .actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
      }
      h2,
      h3,
      p {
        margin: 0;
      }
      article {
        display: grid;
        gap: 0.5rem;
        border: 1px solid var(--custom-border-color);
        border-radius: var(--custom-border-radius, 4px);
        padding: 0.7rem;
      }
      dl {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
        gap: 0.5rem;
        margin: 0;
      }
      dd {
        margin: 0.2rem 0 0;
      }
      label {
        display: grid;
        gap: 0.35rem;
      }
      textarea {
        width: 100%;
      }
      .hint,
      dt {
        color: var(--custom-text-color-muted);
      }
      [role='alert'],
      .issue {
        color: var(--custom-text-color-error);
      }
    `,
  ],
})
export class WorkflowTimersPanelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) workflowId = 0;
  @Input() occurrenceId: number | null = null;
  @Input() previewMode = false;
  @Input() autoLoad = false;
  @Input() viewedETag: string | null = null;
  @Input() definitionVersionId: number | null = null;
  @Input() interactionBlocked = false;
  /** The host selects one exact transport; present unsupported values never fall back to v1. */
  @Input() readVersion: unknown = 1;
  @Output() busyChange = new EventEmitter<boolean>();
  @Output() pendingInputChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<void>();
  @Output() inspectionRequested = new EventEmitter<{
    workflowId: number;
    occurrenceId: number;
  }>();
  private readonly api = inject(WorkflowTimersService);
  private readonly reasonDialogs = inject(WorkflowReasonCaptureDialogService);
  private reasonDialog: WorkflowReasonCaptureDialogHandle | null = null;
  private readRequest?: Subscription;
  private changeRequest?: Subscription;
  private generation = 0;
  private context = '';
  private intent: {
    signature: string;
    key: string;
    request: WorkflowTimerChangeRequest;
  } | null = null;
  rows: ViewedTimer[] = [];
  loaded = false;
  loading = false;
  busy = false;
  stale = false;
  error = '';
  readErrorKind: 'error' | 'forbidden' | null = null;
  notice = '';
  reason = '';
  reasonMode: 'text' | 'catalog' = 'text';
  reasonLabel = '';
  selection: {
    timerId: string;
    revision: number;
    eTag: string;
    operation: WorkflowTimerOperation;
    label: string;
    reason: WorkflowReasonResolution;
    readVersion: 1 | 2;
  } | null = null;
  readonly standalone = { standalone: true };
  stateLabel = workflowTimerStateLabel;
  deadlineBinding(timer: ViewedTimer): WorkflowDeadlineBinding {
    return {
      workflowInstanceId: this.workflowId,
      occurrenceId: this.occurrenceId ?? 0,
      definitionVersionId: this.definitionVersionId,
      sourceId: timer.id,
      sourceRevision: timer.revision,
      generation: timer.generation,
      breachDueUtc: timer.breachDueUtc,
    };
  }
  supportedReadVersion(): 1 | 2 | null {
    return this.readVersion === 1 || this.readVersion === 2
      ? this.readVersion
      : null;
  }
  stageSummary(timer: ViewedTimer): string {
    if (this.readVersion !== 2 || !('version' in timer) || timer.version !== 2)
      return '';
    return [
      !timer.hasWarningAction ? 'Warning: Off' : '',
      !timer.hasBreachAction ? 'Overdue action: None' : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }
  controlsUnavailable(timer: ViewedTimer): string {
    if (
      this.readVersion !== 2 ||
      !('controlsUnavailableReasonCode' in timer) ||
      !timer.controlsUnavailableReasonCode
    )
      return '';
    return timer.controlsUnavailableReasonCode ===
      'workflow.timer.optional_stages_disabled'
      ? 'Controls for optional timer stages are not enabled.'
      : 'Timer controls are unavailable for this visit.';
  }
  private ownerBinding(): WorkflowDeadlineBinding {
    return {
      workflowInstanceId: this.workflowId,
      occurrenceId: this.occurrenceId ?? 0,
      definitionVersionId: this.definitionVersionId,
    };
  }

  ngOnChanges(): void {
    const context = JSON.stringify([
      this.workflowId,
      this.occurrenceId,
      this.previewMode,
      this.definitionVersionId,
      this.readVersion,
    ]);
    if (context === this.context) {
      if (
        this.selection &&
        this.viewedETag &&
        this.selection.eTag !== this.viewedETag
      ) {
        this.stale = true;
        this.intent = null;
      }
      return;
    }
    this.context = context;
    this.reasonDialog?.close(true);
    this.reasonDialog = null;
    this.generation++;
    this.readRequest?.unsubscribe();
    this.changeRequest?.unsubscribe();
    this.rows = [];
    this.loaded = false;
    this.loading = false;
    this.busy = false;
    this.selection = null;
    this.reason = '';
    this.reasonMode = 'text';
    this.reasonLabel = '';
    this.error = '';
    this.readErrorKind = null;
    this.notice = '';
    this.stale = false;
    this.intent = null;
    this.busyChange.emit(false);
    this.pendingInputChange.emit(false);
    if (!this.supportedReadVersion()) {
      this.readErrorKind = 'error';
      this.error =
        'This timer view is unavailable. Refresh the workspace before viewing timers.';
    } else if (this.autoLoad && !this.previewMode) this.refresh();
  }
  ngOnDestroy(): void {
    this.reasonDialog?.close(true);
    this.reasonDialog = null;
    this.generation++;
    this.readRequest?.unsubscribe();
    this.changeRequest?.unsubscribe();
    this.busyChange.emit(false);
    this.pendingInputChange.emit(false);
  }
  refresh(): void {
    const version = this.supportedReadVersion();
    if (
      !version ||
      this.previewMode ||
      this.workflowId < 1 ||
      !this.occurrenceId ||
      this.busy ||
      this.interactionBlocked
    )
      return;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    this.inspectionRequested.emit({
      workflowId: this.workflowId,
      occurrenceId: this.occurrenceId,
    });
    const generation = ++this.generation;
    this.readRequest?.unsubscribe();
    this.rows = [];
    this.loading = true;
    this.error = '';
    this.readErrorKind = null;
    this.selection = null;
    this.intent = null;
    this.stale = false;
    const binding = this.ownerBinding();
    const reading: Observable<ViewedTimer[]> =
      version === 2
        ? this.api.readV2(this.workflowId, this.occurrenceId, binding)
        : this.api.read(this.workflowId, this.occurrenceId);
    this.readRequest = reading.subscribe({
      next: (rows) => {
        if (
          generation !== this.generation ||
          version !== this.readVersion ||
          JSON.stringify(binding) !== JSON.stringify(this.ownerBinding())
        )
          return;
        if (version === 2 && !workflowTimerListV2(rows, binding)) {
          this.loading = false;
          this.loaded = false;
          this.rows = [];
          this.readErrorKind = 'error';
          this.error =
            'Timer details could not be verified. Refresh to try again; your entered reason is kept.';
          return;
        }
        this.rows = rows;
        this.loading = false;
        this.loaded = true;
      },
      error: (error: unknown) => {
        if (
          generation !== this.generation ||
          version !== this.readVersion ||
          JSON.stringify(binding) !== JSON.stringify(this.ownerBinding())
        )
          return;
        this.loading = false;
        this.loaded = false;
        this.rows = [];
        this.readErrorKind =
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
            ? 'forbidden'
            : 'error';
        this.error =
          this.readErrorKind === 'forbidden'
            ? 'You do not have permission to view timers for this visit.'
            : 'Timers could not be loaded. Try again; your entered reason is kept.';
        if (
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
        ) {
          this.reason = '';
          this.reasonLabel = '';
          this.pendingInputChange.emit(false);
        }
      },
    });
  }
  choose(timer: ViewedTimer, operation: string): void {
    const version = this.supportedReadVersion();
    if (
      !version ||
      this.previewMode ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      !['pause', 'resume', 'reset'].includes(operation) ||
      !this.rows.includes(timer) ||
      !timer.availableActions.some((action) => action === operation)
    )
      return;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    const resolution = this.resolveReason(
      timer,
      operation as WorkflowTimerOperation,
    );
    if (resolution.mode !== 'text' || this.reasonMode !== 'text') {
      this.reason = '';
      this.reasonLabel = '';
    }
    this.reasonMode = resolution.mode === 'text' ? 'text' : 'catalog';
    this.selection = {
      timerId: timer.id,
      revision: timer.revision,
      eTag: timer.eTag,
      operation: operation as WorkflowTimerOperation,
      label: timer.label,
      reason: resolution,
      readVersion: version,
    };
    this.intent = null;
    this.error = '';
    this.notice = '';
    this.pendingInputChange.emit(!!this.reason.trim());
  }
  supportedActions(timer: ViewedTimer): string[] {
    return timer.availableActions.filter((action) =>
      ['pause', 'resume', 'reset'].includes(action),
    );
  }
  actionLabel(operation: string): string {
    return (
      (
        {
          pause: 'Pause timer',
          resume: 'Resume timer',
          reset: 'Start new timer generation',
        } as Record<string, string>
      )[operation] ?? 'Unavailable action'
    );
  }
  reasonChanged(reason: string): void {
    if (
      this.busy ||
      (this.selection && this.selection.readVersion !== this.readVersion) ||
      (this.selection?.reason.mode !== 'text' && this.reasonMode !== 'text')
    )
      return;
    this.reason = reason;
    this.intent = null;
    this.pendingInputChange.emit(!!reason.trim());
  }
  reasonPresentation(): WorkflowReasonCapturePresentation | null {
    const selected = this.selection;
    return selected
      ? {
          contract: timerReasonCaptureContract(
            selected.reason.mode !== 'text',
            selected.operation,
          ),
          choices: selected.reason.catalog?.choices || [],
          unavailable: selected.reason.error || undefined,
        }
      : null;
  }
  reasonValue(): WorkflowReasonCaptureValue {
    return this.selection?.reason.mode === 'text'
      ? { code: '', text: this.reason }
      : { code: this.reason, text: '' };
  }
  captureReasonChanged(value: WorkflowReasonCaptureValue): void {
    if (this.selection?.reason.mode === 'text') this.reasonChanged(value.text);
    else this.reasonCodeChanged(value.code);
  }
  editReason(): void {
    const selected = this.selection,
      presentation = this.reasonPresentation();
    if (
      !selected ||
      selected.readVersion !== this.readVersion ||
      !presentation ||
      presentation.unavailable ||
      this.previewMode ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      this.reasonDialog
    )
      return;
    const context = this.context,
      profile = selected.reason.key;
    const handle = this.reasonDialogs.open({
      heading: `${this.actionLabel(selected.operation)} · Reason`,
      presentation,
      value: this.reasonValue(),
      isCurrent: () =>
        context === this.context &&
        selected.readVersion === this.readVersion &&
        this.selection === selected &&
        selected.reason.key === profile &&
        !this.previewMode &&
        !this.busy &&
        !this.interactionBlocked &&
        !this.stale &&
        !this.reasonPresentation()?.unavailable &&
        this.rows.some(
          (timer) =>
            timer.id === selected.timerId &&
            timer.revision === selected.revision &&
            this.resolveReason(timer, selected.operation).key === profile,
        ),
      valueChange: (value) => this.captureReasonChanged(value),
    });
    this.reasonDialog = handle;
    void handle.closed.finally(() => {
      if (this.reasonDialog === handle) this.reasonDialog = null;
    });
  }
  private resolveReason(
    timer: ViewedTimer,
    operation: WorkflowTimerOperation,
  ): WorkflowReasonResolution {
    return resolveTimerReasonCatalog(timer.reasonCatalogs, operation, {
      workflowInstanceId: this.workflowId,
      occurrenceId: this.occurrenceId!,
      timerId: timer.id,
      timerRevision: timer.revision,
      availableOperations: timer.availableActions,
    });
  }
  reasonCodeChanged(code: string): void {
    const selected = this.selection;
    if (
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      selected?.readVersion !== this.readVersion ||
      selected?.reason.mode !== 'catalog' ||
      selected.reason.error ||
      (code !== '' &&
        !selected.reason.catalog?.choices.some(
          (choice) => choice.code === code,
        ))
    )
      return;
    this.reason = code;
    this.reasonLabel =
      selected.reason.catalog?.choices.find((choice) => choice.code === code)
        ?.label || '';
    this.intent = null;
    this.pendingInputChange.emit(!!code);
  }
  canSubmitReason(): boolean {
    const selected = this.selection;
    if (
      !selected ||
      selected.readVersion !== this.readVersion ||
      !this.supportedReadVersion() ||
      !this.reason.trim() ||
      this.reason.trim().length > 500
    )
      return false;
    const source = this.rows.find(
      (timer) =>
        timer.id === selected.timerId && timer.revision === selected.revision,
    );
    if (!source) return false;
    const current = this.resolveReason(source, selected.operation);
    if (
      current.key !== selected.reason.key ||
      current.error ||
      current.mode === 'unavailable'
    )
      return false;
    return (
      current.mode === 'text' ||
      !!current.catalog?.choices.some((choice) => choice.code === this.reason)
    );
  }
  cancel(): void {
    if (this.busy) return;
    this.reasonDialog?.close(true);
    this.reasonDialog = null;
    this.selection = null;
    this.reason = '';
    this.reasonLabel = '';
    this.reasonMode = 'text';
    this.intent = null;
    this.notice = '';
    this.pendingInputChange.emit(false);
  }
  submit(): void {
    if (
      this.previewMode ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      !this.selection ||
      !this.occurrenceId ||
      !this.canSubmitReason()
    )
      return;
    this.reasonDialog?.close();
    this.reasonDialog = null;
    const selected = this.selection;
    const source = this.rows.find(
      (timer) =>
        timer.id === selected.timerId && timer.revision === selected.revision,
    );
    const binding = this.ownerBinding();
    if (
      selected.readVersion === 2 &&
      (!workflowTimerV2(source, binding) ||
        !source.availableActions.includes(selected.operation))
    )
      return;
    const original =
      selected.readVersion === 2
        ? structuredClone(source as WorkflowTimerReadV2)
        : null;
    const request: WorkflowTimerChangeRequest = {
      occurrenceId: this.occurrenceId,
      expectedETag: selected.eTag,
      timerId: selected.timerId,
      expectedRevision: selected.revision,
      operation: selected.operation,
      reason: this.reason.trim(),
      ...(selected.reason.mode === 'catalog'
        ? {
            expectedReasonCatalogHash:
              selected.reason.catalog!.context.catalogHash,
          }
        : {}),
    };
    const signature = JSON.stringify(
      selected.readVersion === 2
        ? [2, this.workflowId, request]
        : [this.workflowId, request],
    );
    if (this.intent?.signature !== signature)
      this.intent = {
        signature,
        key: crypto.randomUUID(),
        request: structuredClone(request),
      };
    const generation = ++this.generation;
    this.readRequest?.unsubscribe();
    this.busy = true;
    this.error = '';
    this.notice = '';
    this.busyChange.emit(true);
    const responseBinding = original
      ? {
          ...binding,
          definitionVersionId: original.deadline.definitionVersionId,
          configurationVersionId: original.deadline.configurationVersionId,
          definitionHash: original.deadline.definitionHash,
        }
      : binding;
    const changing: Observable<ViewedTimer> =
      selected.readVersion === 2
        ? this.api.changeV2(
            this.workflowId,
            this.intent.request,
            this.intent.key,
            responseBinding,
          )
        : this.api.change(
            this.workflowId,
            this.intent.request,
            this.intent.key,
          );
    this.changeRequest = changing.subscribe({
      next: (response) => {
        if (
          generation !== this.generation ||
          selected.readVersion !== this.readVersion ||
          (selected.readVersion === 2 &&
            JSON.stringify(binding) !== JSON.stringify(this.ownerBinding()))
        )
          return;
        if (
          original &&
          !this.acceptsV2Change(
            response,
            original,
            selected.operation,
            responseBinding,
          )
        ) {
          this.changeFailed(
            new Error('The changed timer response could not be verified.'),
            true,
          );
          return;
        }
        this.busy = false;
        this.busyChange.emit(false);
        this.selection = null;
        this.reason = '';
        this.reasonLabel = '';
        this.reasonMode = 'text';
        this.intent = null;
        this.pendingInputChange.emit(false);
        this.notice =
          'Timer change saved. Earlier generations remain in history.';
        this.changed.emit();
        this.refresh();
      },
      error: (error: unknown) => {
        if (
          generation !== this.generation ||
          selected.readVersion !== this.readVersion ||
          (selected.readVersion === 2 &&
            JSON.stringify(binding) !== JSON.stringify(this.ownerBinding()))
        )
          return;
        this.changeFailed(error, selected.readVersion === 2);
      },
    });
  }
  private acceptsV2Change(
    value: unknown,
    original: WorkflowTimerReadV2,
    operation: WorkflowTimerOperation,
    binding: WorkflowDeadlineBinding,
  ): boolean {
    if (!workflowTimerV2(value, { ...binding, isCurrent: true })) return false;
    const previous = original.deadline,
      next = value.deadline;
    if (
      value.code !== original.code ||
      value.label !== original.label ||
      value.ruleVersion !== original.ruleVersion ||
      value.hasWarningAction !== original.hasWarningAction ||
      value.hasBreachAction !== original.hasBreachAction ||
      next.ruleHash !== previous.ruleHash ||
      next.calendarCode !== previous.calendarCode ||
      next.calendarVersionCode !== previous.calendarVersionCode ||
      next.calendarContentHash !== previous.calendarContentHash ||
      next.timeZoneId !== previous.timeZoneId
    )
      return false;
    return operation === 'reset'
      ? value.id.toLowerCase() !== original.id.toLowerCase() &&
          value.generation === original.generation + 1 &&
          value.revision === 1 &&
          value.state === 'active'
      : value.id.toLowerCase() === original.id.toLowerCase() &&
          value.generation === original.generation &&
          value.revision > original.revision &&
          value.state === (operation === 'pause' ? 'paused' : 'active') &&
          value.anchorUtc === original.anchorUtc;
  }
  private changeFailed(error: unknown, version2: boolean): void {
    this.busy = false;
    this.busyChange.emit(false);
    this.error =
      version2 && !(error instanceof HttpErrorResponse)
        ? 'The timer change response could not be verified. Your reason is kept; retry unchanged input or refresh this visit.'
        : this.errorMessage(error);
    if (
      error instanceof HttpErrorResponse &&
      [401, 403].includes(error.status)
    ) {
      this.rows = [];
      this.loaded = false;
      this.selection = null;
      this.intent = null;
      this.reason = '';
      this.reasonLabel = '';
      this.reasonMode = 'text';
      this.pendingInputChange.emit(false);
    }
    if (
      error instanceof HttpErrorResponse &&
      [409, 412, 428].includes(error.status)
    ) {
      this.stale = true;
      this.intent = null;
    }
    if (error instanceof HttpErrorResponse ? error.status === 0 : version2)
      this.notice =
        'The result is uncertain. Retrying unchanged input uses the same request identity.';
  }
  private errorMessage(error: unknown): string {
    const display = mapWorkflowRegistryDiagnosticError(error);
    return `${display.title}: ${display.messages.join(' ')}`;
  }
}
